import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
    Box,
    Flex,
    Button,
    ButtonLink,
    Heading,
    Text,
    TextLink,
    Skeleton,
    Checkbox,
} from '@indeed/ifl-components';
import { AIGeneral, TrashCan } from '@indeed/ifl-icons';
import { Application1, Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import { MarkupComponents } from '@indeed/frontend-markup-react-components';
import type { SystemStyleObject } from '@indeed/ifl-css';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useApolloClient } from '@apollo/client';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { useDeleteAutoApplyApplicationDraft } from '../hooks/useDeleteAutoApplyApplicationDraft';
import { ROOT_TRACKING_KEY_HEADER } from '../../../lib/apollo/headers';
import { resolveShowAiAutofillDisclaimer } from '../review/useShowAiAutofillDisclaimer';
import {
    useFindReviewBeforeSubmitDraftsQuery,
    useCreateAutoApplyPreviewForConfirmationMutation,
    useCreateAutoApplyPreviewForConfirmationWithDqMutation,
    useSubmitAutoApplyApplicationMutation,
    useSubmitReviewBeforeSubmitDraftMutation,
    useConfirmationPageSaveJobMutation,
} from './__generated__/ConfirmationPage.generated';
import GettextFactory from '../../../util/GettextFactory';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { jsProThemeButton } from '../../auto-apply-setup/components/jsProTheme/jsProTheme';
import { NavigateToExternalUrl } from '../components/NavigateToExternalUrl';
import { getCachedPreview, setCachedPreview, type PreviewData } from './previewCache';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { useApplyForMeCopy } from '../../auto-apply-setup/hooks/useApplyForMeCopy';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import { ProgressiveAutomationApplicationButton } from '../components/ProgressiveAutomationApplicationButton';
import { getRumClient } from '../../../lib/rum/rumClient';
import { isNetworkError, logRumError } from '../../../lib/rum/rumErrorLogger';
import { isDqPrefillEnabled } from '../../../lib/proctor/isDqPrefillEnabled';

/**
 * Confirmation Page for Auto Apply
 * Shows summary of applications that will be submitted
 * Design based on Figma: paper airplane illustration with simple job list.
 * Behind the rnhp_auto_apply_banner_tst === 2 (progressive automation) proctor
 * the page renders a redesigned variant: no illustration, each application in
 * its own bordered card, and updated legal/terms copy.
 *
 * Expects URL parameter: draftIds (comma-separated list of draft IDs)
 */
const ConfirmationPage: React.FC = () => {
    const gt = GettextFactory();
    const navigate = useNavigate();
    const location = useLocation();
    const deleteAutoApplyApplicationDraft = useDeleteAutoApplyApplicationDraft();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasConfirmedApplications, setHasConfirmedApplications] = useState(false);
    const eventLogger = useEventLogger();
    const previewPromisesRef = useRef<Map<string, Promise<PreviewData> | PreviewData>>(new Map());
    const { returnUrl, locale, countryCode } = useInitialState();
    const [submitApplication] = useSubmitAutoApplyApplicationMutation();
    const [submitReviewBeforeSubmitDraft] = useSubmitReviewBeforeSubmitDraftMutation();
    const [saveJob] = useConfirmationPageSaveJobMutation();
    const copy = useApplyForMeCopy();
    const { getGroup } = useProctorGroups();
    const { enterSubFlow } = usePageFlow();
    const apolloClient = useApolloClient();

    // rnhp_auto_apply_banner_tst value 2 (progressive_automation) makes each job
    // row a link that opens the application preview (ReviewPage) for that draft
    // and enables the updated legal disclaimer copy on the confirmation page.
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const showDqAiAutofillDisclaimer = isDqPrefillEnabled(getGroup);
    const [createPreviewBase] = useCreateAutoApplyPreviewForConfirmationMutation();
    const [createPreviewWithDq] = useCreateAutoApplyPreviewForConfirmationWithDqMutation();
    const createPreview = showDqAiAutofillDisclaimer ? createPreviewWithDq : createPreviewBase;
    // Renders a legal disclaimer text link that opens in a new tab and logs the click.
    const makeLegalLink =
        (href: string, button: string) =>
        (children: React.ReactNode): React.ReactElement => (
            <TextLink
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                inline
                onClick={() => {
                    eventLogger.logReviewButtonClick?.({
                        currentPage: location.pathname,
                        button,
                    });
                }}
            >
                {children}
            </TextLink>
        );

    // Parse draft IDs from URL parameter
    // Expected format: ?draftIds=draft-1,draft-2,draft-3
    const draftIdsParam = searchParams.get('draftIds');
    const draftIds = useMemo(
        () => (draftIdsParam ? draftIdsParam.split(',').filter(Boolean) : []),
        [draftIdsParam],
    );

    // Parse all failed draft IDs from URL parameters and merge into single Set
    // Includes both GraphQL failures (failedDraftIds) and preview failures (previewFailedDraftIds)
    const failedDraftIdsParam = searchParams.get('failedDraftIds');
    const previewFailedDraftIdsParam = searchParams.get('previewFailedDraftIds');
    const allFailedDraftIdsSet = useMemo(() => {
        const failedIds = failedDraftIdsParam ? failedDraftIdsParam.split(',').filter(Boolean) : [];
        const previewFailedIds = previewFailedDraftIdsParam
            ? previewFailedDraftIdsParam.split(',').filter(Boolean)
            : [];
        return new Set([...failedIds, ...previewFailedIds]);
    }, [failedDraftIdsParam, previewFailedDraftIdsParam]);

    // Filter out all failed drafts - only show drafts we're querying
    const draftIdsToShow = useMemo(
        () => draftIds.filter((id) => !allFailedDraftIdsSet.has(id)),
        [draftIds, allFailedDraftIdsSet],
    );

    // Helmet content - reused across all states
    const helmet = (
        <Helmet>
            <title>{copy.autoApplyConfirmationTitle}</title>
            <meta
                name="description"
                content={gt.gettext('Review and submit your automatic job applications')}
            />
        </Helmet>
    );

    // Using network-only policy to avoid edge case scenario.
    // `state` field is important on this page and it's mutable.
    const { data, loading, error } = useFindReviewBeforeSubmitDraftsQuery({
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
        skip: draftIds.length === 0,
    });

    const applicationsMap = useMemo(
        () =>
            new Map(
                (data?.findReviewBeforeSubmitDrafts?.drafts || [])
                    .filter(({ applicationDraftId }) => {
                        return draftIdsToShow.includes(applicationDraftId);
                    })
                    .map(
                        ({
                            applicationDraftId,
                            jobTitle,
                            jobLocation,
                            applyableJob,
                            iaUid,
                            state,
                            lastPreviewId,
                            employerName,
                            proctors,
                            deferMonetizationSignals,
                        }) => [
                            applicationDraftId,
                            {
                                applicationDraftId,
                                jobTitle,
                                company: employerName,
                                location: jobLocation,
                                iaUid,
                                state,
                                lastPreviewId,
                                clientJobRef: applyableJob.clientJobRef,
                                proctors,
                                deferMonetizationSignals,
                            },
                        ],
                    ),
            ),
        [data?.findReviewBeforeSubmitDrafts?.drafts, draftIdsToShow],
    );

    useEffect(() => {
        if (!loading && !error && data) {
            draftIds.forEach((draftId) => {
                if (!applicationsMap.has(draftId)) {
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'draft-load-error' },
                        errorMessage: {
                            string: 'Draft not found in query response',
                        },
                        draftId: { string: draftId },
                        graphqlOperation: { string: 'findReviewBeforeSubmitDrafts' },
                    });
                }
            });
        }
    }, [loading, applicationsMap, error, data, location.pathname, eventLogger, draftIds]);

    // Create preview promises when data loads or applications change.
    useEffect(() => {
        if (loading) {
            return;
        }

        previewPromisesRef.current = new Map(
            [...applicationsMap.values()].map((application) => {
                const { applicationDraftId, iaUid, state, lastPreviewId, proctors } = application;
                if (previewPromisesRef.current.has(applicationDraftId)) {
                    return [
                        applicationDraftId,
                        previewPromisesRef.current.get(applicationDraftId)!,
                    ];
                }

                // Try to load from cache first
                const cachedPreview = getCachedPreview(applicationDraftId);

                // Reuse only the server's latest preview, as on ReviewPage.
                // Deferred submission also needs complete identifiers; display-only DQ HTML
                // is not required here, even when it was lost on reload.
                const validCachedPreview =
                    state === 'PREVIEW' &&
                    lastPreviewId &&
                    cachedPreview?.previewId === lastPreviewId &&
                    (!application.deferMonetizationSignals ||
                        (cachedPreview.encryptedApplicationId &&
                            cachedPreview.previewHtmlFileToken &&
                            cachedPreview.previewHtmlContentHash))
                        ? cachedPreview
                        : null;

                const previewPromise: Promise<PreviewData> | PreviewData =
                    validCachedPreview ||
                    // Create preview via GraphQL mutation. Compute `showAiAutofillDisclaimer` the same
                    // way ReviewPage did (shared prefill query + helper) so a regenerated preview —
                    // which is what gets submitted — carries the disclaimer the review page decided on,
                    // instead of always hardcoding it on.
                    resolveShowAiAutofillDisclaimer(apolloClient, applicationDraftId, iaUid).then(
                        (showAiAutofillDisclaimer) =>
                            createPreview({
                                variables: {
                                    input: {
                                        draftManagementTelemetry: {
                                            proctorGroups: proctors
                                                .split(',')
                                                .map((allocation) => allocation.trim())
                                                .filter(Boolean),
                                            locale,
                                            countryCode,
                                        },
                                        showAiAutofillDisclaimer,
                                        showDqAiAutofillDisclaimer,
                                    },
                                    smartApplyApplicationStateInput: { id: applicationDraftId },
                                },
                                context: {
                                    headers: {
                                        ...(iaUid && { [ROOT_TRACKING_KEY_HEADER]: iaUid }),
                                    },
                                },
                            }).then((result) => {
                                const previewData =
                                    result.data?.smartApplyApplicationState.createPreview;
                                if (previewData) {
                                    const demographicQuestionAnswersHTML =
                                        'demographicQuestionAnswersHTML' in previewData &&
                                        typeof previewData.demographicQuestionAnswersHTML ===
                                            'string'
                                            ? previewData.demographicQuestionAnswersHTML
                                            : null;
                                    const newPreviewData: PreviewData = {
                                        previewId: previewData.previewId ?? '',
                                        previewHtmlFileToken: previewData.previewHtmlFileToken,
                                        encryptedApplicationId:
                                            previewData.encryptedApplicationId ?? '',
                                        previewHtmlContentHash: previewData.previewHtmlContentHash,
                                        demographicQuestionAnswersHTML: showDqAiAutofillDisclaimer
                                            ? demographicQuestionAnswersHTML
                                            : undefined,
                                    };

                                    // Share the preview with ReviewPage so its preview links
                                    // reuse it instead of regenerating. Only the progressive-
                                    // automation variant exposes those links.
                                    if (isProgressiveAutomation) {
                                        setCachedPreview(applicationDraftId, newPreviewData);
                                    }

                                    return newPreviewData;
                                }
                                throw new Error(
                                    `Failed to create preview for draft ${applicationDraftId}`,
                                );
                            }),
                    );

                // Mark deferred promises as handled to prevent unhandled rejection
                // warnings. Promise.allSettled in handleSubmit still correctly sees
                // rejected promises when it consumes them later.
                if (previewPromise instanceof Promise) {
                    previewPromise.catch(() => {});
                }

                return [applicationDraftId, previewPromise];
            }),
        );
    }, [
        loading,
        applicationsMap,
        locale,
        countryCode,
        createPreview,
        isProgressiveAutomation,
        apolloClient,
        showDqAiAutofillDisclaimer,
    ]);

    const handleRemoveApplication = (applicationDraftId: string, jobKey?: string | null) => {
        const remainingDraftCount = draftIds.filter((id) => id !== applicationDraftId).length;
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'remove-application',
            draftId: { string: applicationDraftId },
        });
        eventLogger.logReviewDraftAction?.({
            currentPage: location.pathname,
            action: 'delete-draft',
            draftId: applicationDraftId,
            remainingDraftCount,
        });
        // Fire-and-forget (the hook now rejects on a failed delete) — swallow it here to preserve
        // the existing optimistic-remove behavior on this page.
        void deleteAutoApplyApplicationDraft(applicationDraftId, jobKey).catch(() => {});
        previewPromisesRef.current.delete(applicationDraftId);
        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set(
                    'draftIds',
                    draftIds.filter((id) => id !== applicationDraftId).join(','),
                );
                return newParams;
            },
            { replace: true },
        );
    };

    const handleSubmit = async () => {
        // Preview promises are populated once the draft data resolves. Guard here as well as on
        // the button so a click while loading can't submit an empty set and navigate to success.
        if (loading) return;

        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'submit-applications',
        });
        setIsSubmitting(true);

        const allEntries = Array.from(previewPromisesRef.current);

        // Settle preview promises first so preview failures are not conflated
        // with submission failures in the event log.
        const previewResults = await Promise.allSettled(
            allEntries.map(([, previewPromise]) => Promise.resolve(previewPromise)),
        );

        const submittableEntries: [string, PreviewData][] = [];
        const previewFailedDraftIds: string[] = [];

        allEntries.forEach(([draftId], i) => {
            const result = previewResults[i];
            if (result.status === 'fulfilled') {
                submittableEntries.push([draftId, result.value]);
            } else {
                // A failed speculative preview counts only once it blocks submission.
                logRumError(getRumClient(), result.reason, {
                    customErrorType: 'AutoApplyPreviewError',
                    sanitizedMessage: 'AutoApplyPreviewError',
                    isPageFailure: !isNetworkError(result.reason),
                });
                previewFailedDraftIds.push(draftId);
            }
        });

        // Only attempt submission for drafts whose preview succeeded
        const submitResults = await Promise.allSettled(
            submittableEntries.map(async ([draftId, preview]) => {
                try {
                    const application = applicationsMap.get(draftId);
                    const iaUid = application?.iaUid;
                    const context = {
                        headers: {
                            ...(iaUid && { [ROOT_TRACKING_KEY_HEADER]: iaUid }),
                        },
                    };

                    if (application?.deferMonetizationSignals) {
                        if (
                            !preview.encryptedApplicationId ||
                            !preview.previewId ||
                            !preview.previewHtmlFileToken ||
                            !preview.previewHtmlContentHash
                        ) {
                            logRumError(getRumClient(), undefined, {
                                customErrorType: 'AutoApplyPreviewError',
                                sanitizedMessage: 'AutoApplyPreviewError',
                                isPageFailure: true,
                            });
                            throw new Error(
                                `Preview for draft ${draftId} is missing the identifiers required to submit with deferred monetization signals`,
                            );
                        }

                        const result = await submitReviewBeforeSubmitDraft({
                            variables: {
                                input: {
                                    applicationDraftId: draftId,
                                    applyId: preview.encryptedApplicationId,
                                    previewId: preview.previewId,
                                    previewHtmlContentHash: preview.previewHtmlContentHash,
                                    previewHtmlFileToken: preview.previewHtmlFileToken,
                                },
                            },
                            context,
                        });

                        if (
                            result.errors?.length ||
                            !result.data?.autoApplyMutations.submitReviewBeforeSubmitDraft?.success
                        ) {
                            throw new Error(
                                `Failed to submit draft ${draftId} with deferred monetization signals`,
                            );
                        }
                    } else {
                        const result = await submitApplication({
                            variables: {
                                input: {
                                    applyId: preview.encryptedApplicationId,
                                    previewId: preview.previewId,
                                    previewHtmlContentHash: preview.previewHtmlContentHash,
                                    previewHtmlFileToken: preview.previewHtmlFileToken,
                                },
                                smartApplyApplicationStateInput: { id: draftId },
                            },
                            context,
                        });

                        if (
                            result.errors?.length ||
                            !result.data?.smartApplyApplicationState.submitApplication
                        ) {
                            throw new Error(`Failed to submit draft ${draftId}`);
                        }
                    }
                } catch (err) {
                    const application = applicationsMap.get(draftId);
                    if (application?.clientJobRef) {
                        try {
                            await saveJob({
                                variables: {
                                    input: { jobKey: application.clientJobRef, state: 'SAVED' },
                                },
                            });
                        } catch {
                            // ignored
                        }
                    }
                    throw err;
                }
            }),
        );

        const failedSubmitDraftIds = submittableEntries
            .filter((_, i) => submitResults[i].status === 'rejected')
            .map(([draftId]) => draftId);
        const allFailedDraftIds = [...previewFailedDraftIds, ...failedSubmitDraftIds];
        const successCount = submitResults.filter((r) => r.status === 'fulfilled').length;

        eventLogger.logReviewSubmit?.({
            totalApplications: allEntries.length,
            draftIds: allEntries.map(([draftId]) => draftId),
            successCount,
            failureCount: allFailedDraftIds.length,
            failedDraftIds: allFailedDraftIds.length > 0 ? { array: allFailedDraftIds } : null,
        });

        // Update search params to only include successfully loaded drafts
        const newParams = new URLSearchParams(searchParams);
        newParams.set('draftIds', [...applicationsMap.keys()].join(','));
        void navigate(`/auto-apply/success?${newParams.toString()}`);
    };

    if (draftIds.length === 0) {
        // User manually removed all drafts
        return <NavigateToExternalUrl url={returnUrl} />;
    }

    if (!loading && applicationsMap.size === 0) {
        // Error UI for when no applications are found
        return (
            <PageTransition pageKey="auto-apply-confirmation">
                {helmet}

                <Flex
                    sx={{
                        flexDirection: 'column',
                        minBlockSize: '100vh',
                        paddingInline: 4,
                        paddingBlockEnd: 6,
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Error content centered */}
                    <Flex
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                            textAlign: 'center',
                            gap: 4,
                        }}
                    >
                        <Alert9 size="md" aria-hidden />
                        <Heading level={3}>
                            {gt.gettext(
                                "We can't load your applications. You can try again later.",
                            )}
                        </Heading>
                    </Flex>

                    {/* Close button at bottom */}
                    <ButtonLink
                        variant="secondary"
                        full
                        size="md"
                        href={returnUrl}
                        sx={{ boxShadow: 'high' }}
                        onClick={() => {
                            eventLogger.logReviewButtonClick?.({
                                currentPage: location.pathname,
                                button: 'back-to-homepage',
                            });
                        }}
                    >
                        {gt.gettext('Back to homepage')}
                    </ButtonLink>
                </Flex>
            </PageTransition>
        );
    }

    const numberOfNotFoundApplications = Math.max(0, draftIdsToShow.length - applicationsMap.size);

    // Progressive-automation redesign: each application is rendered in its own
    // bordered card. The original variant renders all rows inside a single
    // bordered container, so the per-item border lives on the outer container
    // instead (see listContainerSx below).
    const cardSx: SystemStyleObject = isProgressiveAutomation
        ? {
              border: '1px solid',
              borderColor: 'border.weak',
              borderRadius: 'md',
              paddingBlock: 4,
              paddingInline: 4,
          }
        : {};

    const listContainerSx: SystemStyleObject = isProgressiveAutomation
        ? { flexDirection: 'column', gap: 3, opacity: isSubmitting ? 0.5 : 1 }
        : {
              flexDirection: 'column',
              gap: 4,
              border: '1px solid',
              borderColor: 'border.weak',
              paddingBlock: 4,
              paddingInline: 5,
              borderRadius: 'md',
              opacity: isSubmitting ? 0.5 : 1,
          };

    return (
        <PageTransition pageKey="auto-apply-confirmation">
            {helmet}

            {/* Main Content Container */}
            <Flex
                sx={{
                    flexDirection: 'column',
                    paddingInline: 5,
                }}
            >
                <Heading
                    level={4}
                    sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        paddingBlock: 4,
                        marginBlock: 0,
                    }}
                >
                    {isSubmitting ? gt.gettext('Submitting...') : gt.gettext('Ready to submit?')}
                </Heading>

                {/* Original variant only: paper airplane illustration */}
                {!isProgressiveAutomation && (
                    <Flex sx={{ justifyContent: 'center' }}>
                        <Application1
                            size="md"
                            aria-hidden
                            sx={{
                                display: 'flex',
                            }}
                        />
                    </Flex>
                )}

                {isProgressiveAutomation && (
                    <Flex
                        sx={{
                            alignItems: 'flex-start',
                            gap: 3,
                            paddingBlockEnd: 4,
                        }}
                    >
                        <AIGeneral
                            size="xs"
                            color="foreground.strong"
                            aria-hidden
                            sx={{ flexShrink: 0 }}
                        />
                        <Text level={2} variant="secondary">
                            {gt.gettext(
                                'AI may autofill application questions using your profile and past answers. Review before you continue.',
                            )}
                        </Text>
                    </Flex>
                )}

                {/* Application List */}
                <Flex sx={listContainerSx}>
                    {loading ? (
                        // Show skeleton rows while loading
                        draftIdsToShow.map((_, index) => (
                            <Flex
                                key={index}
                                sx={{
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    ...cardSx,
                                }}
                            >
                                <Box sx={{ flex: 1, paddingBlock: '1px' }}>
                                    <Skeleton
                                        sx={{
                                            inlineSize: '80%',
                                            blockSize: '1rem',
                                            marginBlockEnd: 2,
                                        }}
                                    />
                                    <Skeleton sx={{ inlineSize: '60%', blockSize: '1rem' }} />
                                </Box>
                                <Box sx={{ inlineSize: '3.5rem' }}>
                                    <Skeleton sx={{ inlineSize: '2rem', blockSize: '2rem' }} />
                                </Box>
                            </Flex>
                        ))
                    ) : (
                        <>
                            {draftIdsToShow.map((draftId) => {
                                const application = applicationsMap.get(draftId);
                                if (!application) {
                                    return null;
                                }

                                const reviewAriaLabel = application.jobTitle
                                    ? gt.gettext('Review {0} application', application.jobTitle)
                                    : gt.gettext('Review this application');

                                const removeAriaLabel = application.jobTitle
                                    ? gt.gettext('Remove {0} application', application.jobTitle)
                                    : gt.gettext('Remove this application');

                                const jobInfo = (
                                    <>
                                        <Text
                                            variant="contrast"
                                            level={2}
                                            sx={{
                                                fontWeight: 'bold',
                                                display: 'block',
                                            }}
                                        >
                                            {application.jobTitle}
                                        </Text>
                                        <Text
                                            level={2}
                                            variant="secondary"
                                            // The row is an IFL text Button, which bolds its content;
                                            // keep this secondary line at normal weight.
                                            sx={{ fontWeight: 'normal' }}
                                        >
                                            {[application.company, application.location]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </Text>
                                    </>
                                );

                                return (
                                    <Box key={draftId} sx={cardSx}>
                                        {isProgressiveAutomation ? (
                                            // Progressive-automation variant: the whole row is a
                                            // link that opens the application preview.
                                            <ProgressiveAutomationApplicationButton
                                                applicationDraftId={application.applicationDraftId}
                                                ariaLabel={reviewAriaLabel}
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                    // Open the draft's review as a sub-flow detour
                                                    // (`/review/edit`) so the review page shows a
                                                    // single "Update" CTA that returns here on exit,
                                                    // instead of the main-spine Previous/Next stepper.
                                                    eventLogger.logReviewButtonClick?.({
                                                        currentPage: location.pathname,
                                                        button: 'open-application-preview',
                                                        draftId: {
                                                            string: application.applicationDraftId,
                                                        },
                                                    });
                                                    void enterSubFlow(
                                                        `/auto-apply/${application.applicationDraftId}/review/edit`,
                                                    );
                                                }}
                                            >
                                                {jobInfo}
                                            </ProgressiveAutomationApplicationButton>
                                        ) : (
                                            // Default variant: row with a delete button.
                                            <Flex
                                                sx={{
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Box sx={{ flex: 1, paddingInlineEnd: 2 }}>
                                                    {jobInfo}
                                                </Box>
                                                {/* Delete Button */}
                                                <Button
                                                    type="button"
                                                    variant="tertiary"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleRemoveApplication(
                                                            application.applicationDraftId,
                                                            application.clientJobRef,
                                                        )
                                                    }
                                                    aria-label={removeAriaLabel}
                                                    sx={jsProThemeButton.tertiary}
                                                    disabled={isSubmitting}
                                                >
                                                    <TrashCan
                                                        size="md"
                                                        variant="stroke"
                                                        aria-hidden
                                                    />
                                                </Button>
                                            </Flex>
                                        )}
                                    </Box>
                                );
                            })}
                            {numberOfNotFoundApplications > 0 ? (
                                <Text variant="alert" level={1}>
                                    {gt.ngettext(
                                        "We're having some trouble loading {0} application.",
                                        "We're having some trouble loading {0} applications.",
                                        numberOfNotFoundApplications,
                                        [numberOfNotFoundApplications],
                                    )}
                                </Text>
                            ) : null}
                        </>
                    )}
                </Flex>

                {/* Legal/Terms Text */}
                <Box sx={{ paddingBlock: 4 }}>
                    {isProgressiveAutomation ? (
                        <>
                            <Checkbox
                                checked={hasConfirmedApplications}
                                onChange={(event) =>
                                    setHasConfirmedApplications(event.target.checked)
                                }
                                disabled={loading || isSubmitting}
                                // Match the label strut to the level-2 child: lines stay 21px.
                                sx={{ paddingBlockEnd: 0, fontSize: 2, lineHeight: 'normal' }}
                            >
                                <Text level={2} variant="secondary">
                                    {gt.gettext(
                                        "I've reviewed these suggested applications and made any desired edits. These applications are ready to submit.",
                                    )}
                                </Text>
                            </Checkbox>
                            <Text
                                level={2}
                                variant="secondary"
                                sx={{ display: 'block', paddingBlockStart: 3 }}
                            >
                                <MarkupComponents
                                    components={{
                                        termsLink: makeLegalLink(
                                            'https://www.indeed.com/legal#tos',
                                            'terms-link',
                                        ),
                                        cookieLink: makeLegalLink(
                                            'https://hrtechprivacy.com/brands/indeed#Cookies',
                                            'cookie-link',
                                        ),
                                        privacyLink: makeLegalLink(
                                            'https://hrtechprivacy.com/brands/indeed#privacypolicy',
                                            'privacy-link',
                                        ),
                                    }}
                                >
                                    {gt.gettext(
                                        'By submitting these application(s), you agree to our [Terms|termsLink] and acknowledge our [Cookie|cookieLink] & [Privacy Policies|privacyLink]. Your application(s) will be processed & analyzed as described in those terms & policies.',
                                    )}
                                </MarkupComponents>
                            </Text>
                        </>
                    ) : (
                        <Text level={1}>
                            <MarkupComponents
                                components={{
                                    termsLink: makeLegalLink(
                                        'https://www.indeed.com/legal',
                                        'terms-link',
                                    ),
                                }}
                            >
                                {gt.gettext(
                                    "By pressing submit application: 1) you agree to our [Terms, Cookie & Privacy Policies|termsLink]; 2) you consent to your application being transmitted to the Employer (Indeed does not guarantee receipt), & processed & analyzed in accordance with its & Indeed's terms & privacy policies; & 3) you acknowledge that when you apply to jobs outside your country it may involve you sending your personal data to countries with lower levels of data protection.",
                                )}
                            </MarkupComponents>
                        </Text>
                    )}
                </Box>

                {/* Submit Button */}
                <Box>
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={
                            loading ||
                            isSubmitting ||
                            (isProgressiveAutomation && !hasConfirmedApplications)
                        }
                        loading={isSubmitting}
                        full
                    >
                        {gt.ngettext(
                            'Submit {0} application',
                            'Submit {0} applications',
                            loading ? draftIdsToShow.length : applicationsMap.size,
                            [loading ? draftIdsToShow.length : applicationsMap.size],
                        )}
                    </Button>
                </Box>
            </Flex>
        </PageTransition>
    );
};

export default ConfirmationPage;
